<template>
  <div>
    <v-card :disabled="isProcessing" :loading="isProcessing">
      <template v-slot:progress>
        <v-progress-linear absolute color="success" height="4" indeterminate></v-progress-linear>
      </template>
      <v-tabs v-model="tab" class="stock-color" left dark icons-and-text>
        <v-tabs-slider></v-tabs-slider>
        <v-tab href="#tab-1">
          <v-toolbar-title>{{ title }}</v-toolbar-title>
        </v-tab>
      </v-tabs>
      <v-tabs-items v-model="tab">
        <v-tab-item value="tab-1">
          <v-card flat>
            <v-card-text>
              <v-form ref="categoryForm" v-model="validForm" @submit.prevent>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="requestData.name"
                      :label="$t('GENERAL.LABELS.NAME') + '*'"
                      :rules="[validations.required()]"
                      :error-messages="validationErrors.name || []"
                      @change="clearValidationError('name')"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <StockProductDepartmentPicker
                      v-model="requestData.stock_product_department_id"
                      :initial-department="selectedDepartment"
                      :label="$t('STOCK.PRODUCT_CATEGORIES.DEPARTMENT') + '*'"
                      :error-messages="validationErrors.stock_product_department_id || []"
                      @change="clearValidationError('stock_product_department_id')"
                    ></StockProductDepartmentPicker>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-autocomplete
                      v-model="requestData.meat_mapping_value_id"
                      :items="meatOptions"
                      item-text="label"
                      item-value="id"
                      :label="$t('STOCK.PRODUCT_CATEGORIES.MEAT_MAPPING')"
                      :loading="isLoadingMeats"
                      :error-messages="validationErrors.meat_mapping_value_id || []"
                      clearable
                      @change="clearValidationError('meat_mapping_value_id')"
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-checkbox
                      v-model="requestData.is_active"
                      class="mt-0 mt-md-5"
                      :label="$t('GENERAL.LABELS.ACTIVE')"
                      color="primary"
                      hide-details
                    ></v-checkbox>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="warning" @click="back">
                {{ $t("GENERAL.LABELS.BACK_BUTTON") }}
              </v-btn>
              <v-btn color="success" @click="store">
                {{ actionText }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
import validations from "@/views/partials/form/Validations.vue";
import StockProductDepartmentPicker from "@/views/partials/stock/StockProductDepartmentPicker.vue";
import userStateService from "@/common/userState.service";
import { SHOW_SNACK } from "@/store/snack.module";
import { GET_MEATS } from "@/store/mappingValues.module";
import {
  CREATE_STOCK_PRODUCT_CATEGORY,
  GET_STOCK_PRODUCT_CATEGORY,
  UPDATE_STOCK_PRODUCT_CATEGORY,
} from "@/store/stock/stockProductCategories.module";

export default {
  name: "StockEditProductCategory",
  components: {
    StockProductDepartmentPicker,
  },
  data() {
    return {
      validations,
      tab: "tab-1",
      isProcessing: false,
      isLoadingMeats: false,
      validForm: false,
      validationErrors: {},
      title: this.$t("STOCK.PRODUCT_CATEGORIES.CREATE"),
      actionText: this.$t("GENERAL.LABELS.CREATE_BUTTON"),
      requestData: {
        name: "",
        stock_product_department_id: null,
        meat_mapping_value_id: null,
        is_active: true,
      },
      selectedDepartment: null,
      meatOptions: [],
    };
  },
  mounted() {
    this.loadMeats();
    this.loadCategory();
  },
  methods: {
    getErrorMessage(error) {
      if (error && error.errors && typeof error.errors === "object") {
        const firstErrorField = Object.keys(error.errors)[0];
        const firstErrorList = error.errors[firstErrorField];
        if (Array.isArray(firstErrorList) && firstErrorList.length) {
          return firstErrorList[0];
        }
      }
      return error?.message || this.$t("GENERAL.UNEXPECTED_ERROR");
    },
    clearValidationError(field) {
      if (this.validationErrors[field]) {
        this.$delete(this.validationErrors, field);
      }
    },
    loadMeats() {
      this.isLoadingMeats = true;
      return this.$store
        .dispatch(GET_MEATS)
        .then((response) => {
          const meats = Array.isArray(response) ? response : [];
          this.meatOptions = meats.map((item) => ({
            id: item.id,
            label: item.value || `Value #${item.id}`,
          }));
        })
        .catch(() => {
          this.meatOptions = [];
        })
        .finally(() => {
          this.isLoadingMeats = false;
        });
    },
    loadCategory() {
      if (!this.$route.params.id) {
        return;
      }

      this.title = this.$t("STOCK.PRODUCT_CATEGORIES.EDIT");
      this.actionText = this.$t("GENERAL.LABELS.EDIT_BUTTON");
      this.isProcessing = true;

      return this.$store
        .dispatch(GET_STOCK_PRODUCT_CATEGORY, this.$route.params.id)
        .then((response) => {
          this.requestData = {
            name: response.name || "",
            stock_product_department_id: response.stock_product_department_id,
            meat_mapping_value_id: response.meat_mapping_value_id || null,
            is_active: !!response.is_active,
          };
          this.selectedDepartment = response.stock_product_department || null;
        })
        .finally(() => {
          this.isProcessing = false;
        });
    },
    back() {
      this.$router.push({ name: "stock-product-categories" });
    },
    store() {
      this.$refs.categoryForm.validate();
      if (!this.validForm) {
        return;
      }

      this.validationErrors = {};

      if (!this.requestData.stock_product_department_id) {
        this.validationErrors.stock_product_department_id = [
          this.$t("GENERAL.VALIDATION.REQUIRED_FIELD"),
        ];
        return;
      }

      let action = CREATE_STOCK_PRODUCT_CATEGORY;
      let payload = this.requestData;

      if (this.$route.params.id) {
        action = UPDATE_STOCK_PRODUCT_CATEGORY;
        payload = {
          id: this.$route.params.id,
          params: this.requestData,
        };
      }

      this.isProcessing = true;
      this.$store
        .dispatch(action, payload)
        .then((response) => {
          const blinkId = response?.id || this.$route.params.id;
          if (blinkId) {
            userStateService.setState(
              "table-state.stock-product-categories-table.blinkRowItemKey",
              blinkId
            );
          }
          this.$store.dispatch(SHOW_SNACK, { message: this.$t("GENERAL.SUCCESS_MESSAGE") });
          this.$router.push({ name: "stock-product-categories" });
        })
        .catch((error) => {
          this.validationErrors = error?.errors || {};
          this.$store.dispatch(SHOW_SNACK, {
            message: this.getErrorMessage(error),
            type: "error",
          });
        })
        .finally(() => {
          this.isProcessing = false;
        });
    },
  },
};

</script>
